import React from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { Badge, Card, CardContent, CardHeader, CardTitle, CopyableId } from '@duck/ui'
import { useTar } from '@/modules'
import { RelativeDateTime } from '@/components/datetime/RelativeDateTime'
import { formatWorkTime } from '@/utils/time.utils'
import {
  getStatusBadgeColor,
  formatChallengeStatus,
  getFeedbackStatusBadgeColor,
  formatFeedbackGrade,
  getWeaponOfChoiceBadgeColor,
  formatWeaponOfChoice,
} from '../adminChallenges.utils'

interface ChallengeDetailCardsProps {
  challengeInfo: ChallengeService.ChallengeInfo
}

/** Reusable label/value row for detail cards. */
const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-0.5 py-1.5 border-b border-border last:border-b-0">
    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
    <div className="text-sm text-foreground">{children}</div>
  </div>
)

export const ChallengeDetailCards: React.FC<ChallengeDetailCardsProps> = ({ challengeInfo }) => {
  const { t } = useTar()
  const { challenge } = challengeInfo

  const statusBadgeClass = getStatusBadgeColor(challenge.status)
  const feedbackStatusBadgeClass = getFeedbackStatusBadgeColor(challenge.feedbackStatus)
  const weapon = challenge.workState?.chosenWeapon

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Status Card */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('challenges.status')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-1.5 border-b border-border">
            <Badge className={statusBadgeClass}>
              {t(formatChallengeStatus(challenge.status))}
            </Badge>
          </div>
          <DetailRow label={t('challenges.feedbackStatus')}>
            <Badge className={feedbackStatusBadgeClass}>
              {challenge.feedbackStatus
                ? challenge.feedbackStatus.charAt(0).toUpperCase() + challenge.feedbackStatus.slice(1)
                : t('challenges.unsubmitted')}
            </Badge>
          </DetailRow>
          <DetailRow label={t('challenges.feedbackGrade')}>
            {formatFeedbackGrade(challenge.feedbackGrade)}
          </DetailRow>
          <DetailRow label={t('challenges.releaseFeedback')}>
            {challenge.feedbackReleaseOverride ? t('common.yes') : t('common.no')}
          </DetailRow>
        </CardContent>
      </Card>

      {/* Timing Card */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('common.time')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRow label={t('challenges.startTime')}>
            <RelativeDateTime isoDateString={challenge.startTime} className="text-sm" />
          </DetailRow>
          <DetailRow label={t('challenges.submittableUntil')}>
            {challenge.submittableUntil ? (
              <RelativeDateTime isoDateString={challenge.submittableUntil} className="text-sm" />
            ) : (
              <span className="text-muted-foreground">{t('common.notApplicable')}</span>
            )}
          </DetailRow>
          <DetailRow label={t('challenges.endTime')}>
            {challenge.endTime ? (
              <RelativeDateTime isoDateString={challenge.endTime} className="text-sm" />
            ) : (
              <span className="text-muted-foreground">{t('common.notApplicable')}</span>
            )}
          </DetailRow>
          <DetailRow label={t('common.workTime')}>
            {formatWorkTime(challenge.workTime, t)}
          </DetailRow>
          <DetailRow label={t('challenges.lastWorkTimeUpdate')}>
            {challenge.lastWorkTimeUpdate ? (
              <RelativeDateTime isoDateString={challenge.lastWorkTimeUpdate} className="text-sm" />
            ) : (
              <span className="text-muted-foreground">{t('common.notApplicable')}</span>
            )}
          </DetailRow>
          <DetailRow label={t('challenges.workState')}>
            {challenge.workState?.savedAt ? (
              <RelativeDateTime isoDateString={challenge.workState.savedAt} className="text-sm" />
            ) : (
              <span className="text-muted-foreground">{t('common.notApplicable')}</span>
            )}
          </DetailRow>
        </CardContent>
      </Card>

      {/* Identity Card */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('common.id')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRow label={t('challenges.challengeId')}>
            <CopyableId value={challenge.id} />
          </DetailRow>
          <DetailRow label={t('challenges.slug')}>
            <span className="font-mono text-xs text-muted-foreground" dir="ltr">
              {challenge.slug}
            </span>
          </DetailRow>
          <DetailRow label={t('challenges.userId')}>
            <CopyableId value={challenge.userId} />
          </DetailRow>
          <DetailRow label={t('challenges.taskId')}>
            <CopyableId value={challenge.taskId} />
          </DetailRow>
          {challenge.assignmentId && (
            <DetailRow label={t('common.assignment')}>
              <CopyableId value={challenge.assignmentId} />
            </DetailRow>
          )}
          {challenge.organizationId && (
            <DetailRow label={t('common.organization')}>
              <CopyableId value={challenge.organizationId} />
            </DetailRow>
          )}
          {weapon && (
            <DetailRow label={t('challenges.weaponOfChoice')}>
              <Badge className={getWeaponOfChoiceBadgeColor(weapon)}>
                {t(formatWeaponOfChoice(weapon))}
              </Badge>
            </DetailRow>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
